import { GoogleMapProps, MarkerDomainTypes } from '@nx-smartmonkey/ui';
import { memo, useMemo } from 'react';

import { GeoFence } from '../../../../domain/geoFence/GeoFence';
import { StopFlat } from '../../../../domain/stop/StopFlat';
import { useSetActiveLanguage } from '../../../../hooks/translations/useSetActiveLanguage';
import { Map } from '../../../../react-components/Map';

interface PlannerStopsMapMapProps extends Omit<GoogleMapProps, `apiKey`> {
  geoFencesEnabled?: boolean;
  flatStops?: StopFlat[];
  projectZones?: GeoFence[];
  geoFenceFilters?: GeoFence[];
  selectedStopsIds?: string[];
}

const PlannerStopsMapMapComponent = ({
  geoFencesEnabled = false,
  flatStops = [],
  projectZones = [],
  geoFenceFilters = [],
  selectedStopsIds = [],
  onContextualMenu,
  ...mapProps
}: PlannerStopsMapMapProps) => {
  const { data: activeLanguage } = useSetActiveLanguage();
  const getMarkers = useMemo(
    () => [
      ...flatStops
        .filter((flatStop) => {
          if (!flatStop.isValidLocation()) {
            return false;
          }

          if (geoFencesEnabled && geoFenceFilters.length > 0) {
            return geoFenceFilters.some((geoFence) =>
              geoFence.isMarkerInsideGeoFence({ lat: flatStop.lat!, lng: flatStop.lng! })
            );
          }

          return true;
        })
        .map((stop: StopFlat) => ({
          id: stop.id,
          label: stop.label,
          selected: selectedStopsIds.includes(stop.id),
          lat: stop.lat!,
          lng: stop.lng!,
          kind: stop.type === `delivery` ? `default` : (`pickup` as MarkerDomainTypes),
        })),
    ],
    [geoFenceFilters, geoFencesEnabled, selectedStopsIds, flatStops]
  );

  const getGeoFences = useMemo(
    () =>
      !geoFencesEnabled
        ? []
        : projectZones.map((geo) => ({
            id: geo.id,
            paths: geo.coordinatesToObject(),
            color: geo.color.value,
          })),
    [geoFencesEnabled, projectZones]
  );

  return (
    <Map
      language={activeLanguage?.locale ?? `en`}
      onContextualMenu={onContextualMenu}
      markers={getMarkers}
      geoFences={getGeoFences}
      {...mapProps}
    />
  );
};

export const PlannerStopsMapMap = memo(PlannerStopsMapMapComponent);
